import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Button, InputAdornment, Paper, Stack, TextField } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RegistryActivityEntry } from '../../api/registryApiTypes';
import DataTable, { type Column } from '../../ui/DataTable';
import { AboutThisPage, PageHeader } from '../../ui/primitives';
import { useRegistryActivity } from './useRegistryActivity';

const RECORD_PATHS: Record<string, string> = {
  Setup: '/manager/setup/',
  Fund: '/manager/funds/',
  Offer: '/manager/offers/',
};

const WHEN = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const COLUMNS: Column<RegistryActivityEntry>[] = [
  {
    key: 'at',
    label: 'When',
    sortValue: (row) => row.at,
    render: (row) => WHEN.format(new Date(row.at)),
  },
  {
    key: 'actor',
    label: 'Who',
    sortValue: (row) => row.actor,
    render: (row) => row.actor,
  },
  { key: 'action', label: 'Action', render: (row) => row.action },
  { key: 'record', label: 'Record', render: (row) => row.record },
];

/** Where a row leads: the record it changed, or Default Values. */
export function activityLink(entry: RegistryActivityEntry): string | null {
  if (entry.recordType === 'Default Values') return '/manager/settings';
  const base = RECORD_PATHS[entry.recordType];
  return base && entry.recordExternalId
    ? `${base}${encodeURIComponent(entry.recordExternalId)}`
    : null;
}

/** The audit trail from the Activity Log (test finding 25). The ledger arrives with issuing. */
export default function LiveAuditPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { shown, remaining, loading, error, loadOlder } = useRegistryActivity(search);

  return (
    <>
      <PageHeader
        title="Audit trail"
        subtitle="Every change made in the registry console, newest first."
      />
      <AboutThisPage>
        <p>
          Each line is an entry in the Fides Activity Log. The main admin Activity Log shows the
          same entries with the fields each change touched. The registry ledger appears here once
          units are issued.
        </p>
      </AboutThisPage>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper variant="outlined">
        <Box sx={{ p: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search who, action or record"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <DataTable
          rows={shown}
          rowKey={(row) => row.externalId}
          onRowClick={(row) => {
            const link = activityLink(row);
            if (link) navigate(link);
          }}
          dense
          pageSize={30}
          emptyMessage={loading ? 'Loading…' : 'No registry activity yet'}
          defaultSort={{ key: 'at', direction: 'desc' }}
          columns={COLUMNS}
        />
        {remaining > 0 && (
          <Stack direction="row" justifyContent="center" sx={{ p: 2 }}>
            <Button disabled={loading} onClick={loadOlder}>
              Load older entries ({remaining} more)
            </Button>
          </Stack>
        )}
      </Paper>
    </>
  );
}
